import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
  attachActors
} from '../utils/notificationService';
import {
  notificationText, notificationLink, notificationIcon,
  timestampMillis, relativeTime, badgeLabel
} from '../utils/notifications';

export default function NotificationBell() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  // One live listener drives both the badge and the panel. Actor profiles are
  // joined afterwards, off the snapshot path, so a slow or failed profile
  // read delays names but never the unread count.
  useEffect(() => {
    if (!currentUser) {
      setRows([]);
      return undefined;
    }
    let active = true;
    const unsubscribe = subscribeToNotifications(currentUser.uid, (next) => {
      if (!active) return;
      setRows(next);
      attachActors(next).then((withActors) => {
        if (active) setRows(withActors);
      });
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [currentUser]);

  const unreadCount = useMemo(() => rows.filter((n) => !n.read).length, [rows]);
  const badge = badgeLabel(unreadCount);

  // Close on an outside click or Escape. Both are needed: the panel overlays
  // page content, and on mobile it sits above a fixed tab bar where there is
  // no obvious "off" target other than the page itself.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Move focus into the panel when it opens so keyboard and screen-reader
  // users land on the list rather than being left behind on the trigger.
  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  if (!currentUser) return null;

  function handleOpen(row) {
    // The pair that matters: notification_open says the bell got attention,
    // notification_click says a notification was worth acting on. The ratio
    // is the only honest measure of whether the bell earns its place.
    track('notification_click', { type: row.type, was_unread: !row.read });
    // Optimistic: the row greys out immediately and the listener confirms.
    // A failed write just means it stays unread, which is recoverable and
    // not worth blocking navigation on.
    if (!row.read) markNotificationRead(row.id).catch(() => {});
    const to = notificationLink(row);
    setOpen(false);
    if (to) navigate(to);
  }

  function handleDismiss(e, row) {
    e.stopPropagation(); // don't also open the notification we're removing
    dismissNotification(row.id).catch((err) =>
      console.error('Error dismissing notification:', err)
    );
  }

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`nav-link notif-trigger ${open ? 'active' : ''}`}
        onClick={() => setOpen((v) => {
          // Only the opening half; a close carries no information.
          if (!v) track('notification_open', { unread: unreadCount });
          return !v;
        })}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : 'Notifications'
        }
      >
        {/* Icon only, no caption. The other tab-bar items are icon+label and
            each take an equal share of the bar; giving the bell a label too
            meant seven labelled items fighting over a 375px phone. Without
            one it claims icon width instead, which is what pays for the Plan
            tab. aria-label on the button carries the name either way. */}
        <span className="notif-icon" aria-hidden="true">
          🔔
          {badge && <span className="notif-badge">{badge}</span>}
        </span>
      </button>

      {open && (
        <div
          className="notif-panel"
          ref={panelRef}
          tabIndex={-1}
          role="region"
          aria-label="Notifications"
        >
          <div className="notif-head">
            <span className="notif-head-title">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notif-mark-all"
                onClick={() => markAllNotificationsRead(rows).catch((err) =>
                  console.error('Error marking notifications read:', err)
                )}
              >
                Mark all read
              </button>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="notif-empty">
              <div className="notif-empty-icon" aria-hidden="true">🔔</div>
              <p className="notif-empty-title">Nothing yet</p>
              <p className="notif-empty-body">
                You'll hear from us when someone follows you, comments on a
                recipe, or gives one five stars.
              </p>
            </div>
          ) : (
            <ul className="notif-list">
              {rows.map((row) => {
                const millis = timestampMillis(row.createdAt);
                return (
                  <li key={row.id}>
                    <div
                      className={`notif-row ${row.read ? '' : 'unread'}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpen(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpen(row);
                        }
                      }}
                    >
                      {row.actorPhotoURL ? (
                        <img
                          className="notif-avatar"
                          src={row.actorPhotoURL}
                          alt=""
                          style={{ objectPosition: row.actorPhotoPosition }}
                          loading="lazy"
                        />
                      ) : (
                        <div className="notif-avatar notif-avatar-fallback" aria-hidden="true">
                          {notificationIcon(row.type)}
                        </div>
                      )}

                      <div className="notif-body">
                        <p className="notif-text">
                          {notificationText(row, row.actorName)}
                        </p>
                        <span className="notif-time">{relativeTime(millis)}</span>
                      </div>

                      {!row.read && <span className="notif-dot" aria-hidden="true" />}

                      <button
                        type="button"
                        className="notif-dismiss"
                        onClick={(e) => handleDismiss(e, row)}
                        aria-label="Dismiss this notification"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
